import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ButtonSpinner } from '../ui/Loader';
import {
  useGetServicesByServerQuery,
  useGetLocationsByServerQuery,
  useGetProvidersByServerQuery,
  useGetPurchaseSummaryMutation,
  usePurchaseNumberMutation,
} from '../../store/api/temporaryNumbersApi';
import { useGetUserProfileQuery } from '../../store/api/userApi';
import { Server, Service, Location, Provider } from '../../types/api';

import { LocationCard } from './LocationCard';
import { ServiceCard } from './ServiceCard';

interface PurchaseWizardProps {
  selectedServer: Server | null;
  disabled?: boolean;
  onPurchaseComplete: (temporaryNumberId: string) => void;
  onCancel: () => void;
}

type WizardStep = 'server' | 'service' | 'location' | 'provider' | 'confirm';

export function PurchaseWizard({
  selectedServer,
  disabled,
  onPurchaseComplete,
  onCancel,
}: PurchaseWizardProps) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<WizardStep>(
    selectedServer ? 'service' : 'server'
  );
  const [server, setServer] = useState<Server | null>(selectedServer);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [summaryPrice, setSummaryPrice] = useState<number | null>(null);
  const [summaryCurrency, setSummaryCurrency] = useState('USD');
  const [summaryLocation, setSummaryLocation] = useState<string | null>(null);
  const [serviceSearch, setServiceSearch] = useState('');
  const [servicePage, setServicePage] = useState(1);
  const [locationSearch, setLocationSearch] = useState('');
  const [locationPage, setLocationPage] = useState(1);
  const itemsPerPage = 10;

  const isCountryServer = server?.serverId === 1 || server?.serverId === 4;

  const { data: userData } = useGetUserProfileQuery(undefined);
  const userBalance = userData?.data.user.balance || 0;

  const { data: servicesData, isLoading: isLoadingServices } =
    useGetServicesByServerQuery(
      { serverId: server?.serverId ?? 0 },
      { skip: !server || currentStep === 'server' }
    );

  // Get serviceId for server 1 (SMSPool) - must be calculated inside the query params
  const locationsQueryParams = useMemo(() => {
    if (import.meta.env.DEV && server?.serverId === 1) {
      console.log('Calculating locationsQueryParams:', {
        hasServer: !!server,
        hasSelectedService: !!selectedService,
        currentStep,
        selectedServiceKeys: selectedService ? Object.keys(selectedService) : null,
        hasID: selectedService && 'ID' in selectedService,
        selectedService,
      });
    }

    if (!server || !selectedService || currentStep !== 'location') {
      if (import.meta.env.DEV && server?.serverId === 1) {
        console.log('locationsQueryParams: returning null because:', {
          noServer: !server,
          noSelectedService: !selectedService,
          wrongStep: currentStep !== 'location',
        });
      }
      return null;
    }
    
    if (server.serverId === 1) {
      // Server 1 (SMSPool) requires serviceId
      let serviceId: string | null = null;

      if ('ID' in selectedService && selectedService.ID != null) {
        serviceId = String(selectedService.ID);
      } else if ('service' in selectedService && selectedService.service != null) {
        serviceId = String(selectedService.service);
      } else if ('sid' in selectedService && selectedService.sid != null) {
        serviceId = String(selectedService.sid);
      } else {
        // Fallback: find any truthy string/number value (skip name, price)
        const svc = selectedService as unknown as Record<string, unknown>;
        const found = Object.entries(svc).find(
          ([k, v]) => k !== 'name' && k !== 'price' && v != null && v !== ''
        );
        if (found) serviceId = String(found[1]);
      }

      if (serviceId) {
        const params = {
          serverId: server.serverId,
          serviceId,
        };
        if (import.meta.env.DEV) {
          console.log('locationsQueryParams: returning params for server 1:', params);
        }
        return params;
      }
      if (import.meta.env.DEV) {
        console.log('locationsQueryParams: server 1 but no ID/service in selectedService', selectedService);
      }
      return null;
    }
    
    // Other servers don't require serviceId
    return {
      serverId: server.serverId,
      serviceId: undefined,
    };
  }, [server, selectedService, currentStep]);

  const {
    data: locationsData,
    isLoading: isLoadingLocations,
    refetch: refetchLocations,
  } = useGetLocationsByServerQuery(
    locationsQueryParams || { serverId: 1, serviceId: '' },
    {
      skip: !locationsQueryParams,
    }
  );

  const { data: providersData, isLoading: isLoadingProviders } = useGetProvidersByServerQuery(
    { serverId: server?.serverId ?? 0 },
    { skip: !server || server.serverId !== 5 || currentStep !== 'provider' }
  );

  // Refetch locations when step changes to location and we have valid params
  useEffect(() => {
    if (
      currentStep === 'location' &&
      locationsQueryParams &&
      server?.serverId === 1
    ) {
      // Small delay to ensure state is updated
      const timer = setTimeout(() => {
        if (import.meta.env.DEV) {
          console.log('Refetching locations with params:', locationsQueryParams);
        }
        void refetchLocations();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentStep, locationsQueryParams, refetchLocations, server?.serverId]);

  // Debug: Log query params for server 1
  if (import.meta.env.DEV && server?.serverId === 1) {
    console.log('Server 1 Locations Debug:', {
      currentStep,
      hasServer: !!server,
      hasSelectedService: !!selectedService,
      selectedService,
      locationsQueryParams,
      shouldSkip: !locationsQueryParams,
      isLoadingLocations,
    });
  }

  const [purchaseNumber, { isLoading: isPurchasing }] = usePurchaseNumberMutation();
  const [getPurchaseSummary, { isLoading: isLoadingSummary }] = useGetPurchaseSummaryMutation();

  useEffect(() => {
    if (selectedServer && currentStep === 'server') {
      setServer(selectedServer);
      setCurrentStep('service');
      setSelectedService(null);
      setSelectedLocation(null);
      setSelectedProvider(null);
      setSummaryPrice(null);
      setSummaryLocation(null);
      setServiceSearch('');
      setServicePage(1);
      setLocationSearch('');
      setLocationPage(1);
    }
  }, [selectedServer, currentStep]);

  // Reset search and pagination when step changes
  useEffect(() => {
    if (currentStep === 'service') {
      setServiceSearch('');
      setServicePage(1);
    } else if (currentStep === 'location') {
      setLocationSearch('');
      setLocationPage(1);
      
      // Debug: Log when location step is reached
      if (import.meta.env.DEV && server?.serverId === 1) {
        console.log('Location step reached:', {
          server,
          selectedService,
          hasID: selectedService && 'ID' in selectedService,
        });
      }
    }
  }, [currentStep, server, selectedService]);


  const handleServiceSelect = (service: Service) => {
    setSelectedService(service);
    setCurrentStep('location');
    setSelectedLocation(null);
    setSelectedProvider(null);
    setSummaryPrice(null);
    setSummaryLocation(null);
    setLocationSearch('');
    setLocationPage(1);
  };

  const handleLocationSelect = (location: Location) => {
    if (import.meta.env.DEV && server?.serverId === 1) {
      console.log('Location selected:', location);
      console.log('Has country:', 'country' in location);
      console.log('Location keys:', Object.keys(location));
    }
    setSelectedLocation(location);
    setCurrentStep(server?.serverId === 5 ? 'provider' : 'confirm');
  };

  const handleSkipLocation = () => {
    setSelectedLocation(null);
    setCurrentStep(server?.serverId === 5 ? 'provider' : 'confirm');
  };

  const handleProviderSelect = (provider: Provider) => {
    setSelectedProvider(provider);
    setCurrentStep('confirm');
  };

  const getServicePrice = (): number | null => {
    if (!selectedService) return null;
    if ('price' in selectedService) {
      return selectedService.price;
    }
    return null;
  };

  const getLocationPrice = (): number | null => {
    if (!selectedLocation) return null;
    if ('price' in selectedLocation && selectedLocation.price != null && selectedLocation.price !== '') {
      const num = parseFloat(String(selectedLocation.price));
      return Number.isFinite(num) ? num : null;
    }
    return null;
  };

  const getFinalPrice = (): number | null => {
    if ((server?.serverId === 5 || server?.serverId === 6) && summaryPrice !== null) {
      return summaryPrice;
    }
    const locationPrice = getLocationPrice();
    if (locationPrice !== null) return locationPrice;
    return getServicePrice();
  };

  useEffect(() => {
    const loadSummary = async () => {
      if (currentStep !== 'confirm' || !server || !selectedService) return;

      const serverId = server.serverId;
      if (serverId !== 5 && serverId !== 6) return;

      if (serverId === 5) {
        const serviceId =
          'id' in selectedService
            ? String(selectedService.id)
            : 'ID' in selectedService
            ? String(selectedService.ID)
            : '';
        if (!serviceId) return;

        const locationCode =
          selectedLocation && 'areaCode' in selectedLocation
            ? String((selectedLocation as { areaCode?: string }).areaCode ?? '')
            : selectedLocation && 'code' in selectedLocation
            ? String((selectedLocation as { code?: string }).code ?? '')
            : undefined;

        try {
          const response = await getPurchaseSummary({
            serverId: 5,
            serviceId,
            locationCode: locationCode || undefined,
            stateCode: selectedProvider?.id,
          }).unwrap();

          setSummaryPrice(response.data.price);
          setSummaryCurrency(response.data.currency || 'USD');
          setSummaryLocation(response.data.location || null);
        } catch {
          setSummaryPrice(null);
        }
        return;
      }

      if (serverId === 6) {
        const serviceId =
          '_id' in selectedService
            ? String((selectedService as { _id: string })._id)
            : '';
        if (!serviceId) return;

        const areaCode =
          selectedLocation && 'areaCode' in selectedLocation
            ? String((selectedLocation as { areaCode?: string }).areaCode ?? '')
            : undefined;

        try {
          const response = await getPurchaseSummary({
            serverId: 6,
            serviceId,
            areaCode: areaCode || undefined,
          }).unwrap();

          setSummaryPrice(response.data.price);
          setSummaryCurrency(response.data.currency || 'USD');
          setSummaryLocation(response.data.location || null);
        } catch {
          setSummaryPrice(null);
        }
      }
    };

    void loadSummary();
  }, [currentStep, server, selectedService, selectedLocation, selectedProvider, getPurchaseSummary]);

  const handlePurchase = async () => {
    // Debug: Log all state before purchase
    if (import.meta.env.DEV && server?.serverId === 1) {
      console.log('=== Purchase Debug ===');
      console.log('Server:', server);
      console.log('Selected Service:', selectedService);
      console.log('Selected Location:', selectedLocation);
      console.log('Has selectedLocation:', !!selectedLocation);
      if (selectedLocation) {
        console.log('Location type:', typeof selectedLocation);
        console.log('Location keys:', Object.keys(selectedLocation));
        console.log('Has country:', 'country' in selectedLocation);
        console.log('Has country_id:', 'country_id' in selectedLocation);
        if ('country' in selectedLocation) {
          console.log('Country value:', (selectedLocation as { country: string }).country);
        }
      }
    }

    // Validate required fields
    if (!server) {
      if (import.meta.env.DEV) {
        console.error('Missing server');
      }
      toast.error(t('errors.network'));
      return;
    }

    if (!selectedService) {
      if (import.meta.env.DEV) {
        console.error('Missing selected service');
      }
      toast.error(t('temporaryNumbers.purchase.serviceRequired'));
      return;
    }

    if (!selectedLocation && server.serverId !== 5 && server.serverId !== 6) {
      if (import.meta.env.DEV) {
        console.error('Missing selected location');
      }
      toast.error(isCountryServer ? t('temporaryNumbers.purchase.countryRequired') : t('temporaryNumbers.purchase.stateRequired'));
      return;
    }

    const price = getFinalPrice();
    if (price !== null && userBalance < price) {
      toast.error(t('temporaryNumbers.purchase.insufficientBalance'));
      return;
    }

    const selectedLocationValue = selectedLocation;

    try {
      // Build purchase request based on server type
      let purchaseData: {
        serverId: number;
        serviceId: string;
        locationCode?: string;
        stateCode?: string;
      } | null = null;

      if (server.serverId === 1) {
        // SMSPool - requires locationCode (country name)
        let smspoolServiceId: string | null = null;
        if ('ID' in selectedService && selectedService.ID != null) {
          smspoolServiceId = String(selectedService.ID);
        } else if ('service' in selectedService && selectedService.service != null) {
          smspoolServiceId = String(selectedService.service);
        } else if ('sid' in selectedService && selectedService.sid != null) {
          smspoolServiceId = String(selectedService.sid);
        } else {
          const svc = selectedService as unknown as Record<string, unknown>;
          const found = Object.entries(svc).find(
            ([k, v]) => k !== 'name' && k !== 'price' && v != null && v !== ''
          );
          if (found) smspoolServiceId = String(found[1]);
        }

        if (!smspoolServiceId) {
          toast.error(t('temporaryNumbers.purchase.serviceRequired'));
          return;
        }

        if (!selectedLocationValue) {
          toast.error(t('temporaryNumbers.purchase.countryRequired'));
          return;
        }

        // Check if location has country property (for SMSPool)
        const hasCountry = 'country' in selectedLocationValue;
        const hasName = 'name' in selectedLocationValue;

        if (import.meta.env.DEV) {
          console.log('Location check:', {
            hasCountry,
            hasName,
            location: selectedLocationValue,
            keys: Object.keys(selectedLocationValue),
          });
        }

        let locationCode: string | null = null;

        // Try to get country from location - multiple fallback strategies
        const locationAny = selectedLocationValue as unknown as Record<string, unknown>;

        // Strategy 1: Direct country property
        if ('country' in locationAny && typeof locationAny.country === 'string') {
          locationCode = locationAny.country;
        }
        // Strategy 2: name property (fallback)
        else if ('name' in locationAny && typeof locationAny.name === 'string') {
          locationCode = locationAny.name;
        }
        // Strategy 3: Try to get any string value
        else {
          const stringValues = Object.values(locationAny).filter(
            (v) => typeof v === 'string' && v.trim() !== ''
          ) as string[];
          if (stringValues.length > 0) {
            locationCode = stringValues[0] || null; // Use first string value
          }
        }

        if (import.meta.env.DEV) {
          console.log('Location code extraction:', {
            locationCode,
            hasCountry,
            hasName,
            location: selectedLocationValue,
            allKeys: Object.keys(selectedLocationValue),
            allValues: Object.values(selectedLocationValue),
          });
        }

        if (!locationCode || typeof locationCode !== 'string' || locationCode.trim() === '') {
          if (import.meta.env.DEV) {
            console.error('Location code is invalid:', {
              locationCode,
              hasCountry,
              hasName,
              location: selectedLocationValue,
              locationType: typeof selectedLocationValue,
              locationKeys: Object.keys(selectedLocationValue),
            });
          }
          toast.error(t('temporaryNumbers.purchase.locationRequired'));
          return;
        }

        purchaseData = {
          serverId: server.serverId,
          serviceId: smspoolServiceId,
          locationCode: locationCode.trim(),
        };

        if (import.meta.env.DEV) {
          console.log('Final Purchase Data:', purchaseData);
        }
      } else if (server.serverId === 2 || server.serverId === 3) {
        // PVA Deals or Tellabot
        if (selectedLocationValue && 'id' in selectedService && 'value' in selectedLocationValue) {
          purchaseData = {
            serverId: server.serverId,
            serviceId: String(selectedService.id),
            stateCode: selectedLocationValue.value,
          };
        }
      } else if (server.serverId === 4) {
        // DurianRCS
        if (selectedLocationValue && 'pid' in selectedService && 'code' in selectedLocationValue) {
          purchaseData = {
            serverId: server.serverId,
            serviceId: String(selectedService.pid),
            locationCode: selectedLocationValue.code,
          };
        }
      } else if (server.serverId === 5) {
        if (!('id' in selectedService)) {
          toast.error(t('temporaryNumbers.purchase.serviceRequired'));
          return;
        }

        const locationCode =
          selectedLocation && 'areaCode' in selectedLocation
            ? String((selectedLocation as { areaCode?: string }).areaCode ?? '')
            : undefined;

        purchaseData = {
          serverId: server.serverId,
          serviceId: String(selectedService.id),
          locationCode: locationCode || undefined,
          stateCode: selectedProvider?.id,
        };
      } else if (server.serverId === 6) {
        if (!('_id' in selectedService)) {
          toast.error(t('temporaryNumbers.purchase.serviceRequired'));
          return;
        }

        const areaCode =
          selectedLocation && 'areaCode' in selectedLocation
            ? String((selectedLocation as { areaCode?: string }).areaCode ?? '')
            : undefined;

        purchaseData = {
          serverId: server.serverId,
          serviceId: String((selectedService as { _id: string })._id),
          areaCode: areaCode || undefined,
        };
      }

      if (!purchaseData) {
        toast.error(t('errors.network'));
        return;
      }

      const result = await purchaseNumber(purchaseData).unwrap();
      toast.success(t('temporaryNumbers.purchase.success'));
      onPurchaseComplete(result.data.temporaryNumber._id);
    } catch (error) {
      const err = error as { data?: { message?: string }; message?: string };
      toast.error(
        err?.data?.message || err?.message || t('errors.network')
      );
    }
  };

  const handleBack = () => {
    if (currentStep === 'confirm') {
      if (server?.serverId === 5) {
        setCurrentStep('provider');
      } else {
        setCurrentStep('location');
      }
    } else if (currentStep === 'provider') {
      setCurrentStep('location');
      setSelectedProvider(null);
    } else if (currentStep === 'location') {
      setCurrentStep('service');
      setSelectedLocation(null);
    } else if (currentStep === 'service') {
      if (selectedServer) {
        onCancel();
      } else {
        setCurrentStep('server');
        setSelectedService(null);
      }
    }
  };

  const totalSteps = useMemo(() => {
    if (!server) return 4;
    if (server.serverId === 5) return 5;
    return 4;
  }, [server]);

  const renderStep = () => {
    switch (currentStep) {
      case 'server':
        return (
          <div className="text-center py-8">
            <p className="text-neutral-medium font-body">
              {t('temporaryNumbers.purchase.step1')}
            </p>
          </div>
        );

      case 'service': {
        if (isLoadingServices) {
          return (
            <div className="text-center py-8">
              <p className="text-neutral-medium font-body animate-pulse">
                {t('common.loading')}
              </p>
            </div>
          );
        }

        if (disabled) {
          return (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/20 mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <p className="text-xl font-heading font-semibold text-red-400 mb-2">
                {t('temporaryNumbers.purchase.serverDisabled')}
              </p>
              <p className="text-neutral-medium font-body">
                {t('temporaryNumbers.purchase.serverDisabledDesc')}
              </p>
            </div>
          );
        }

        const allServices = servicesData?.data.services || [];
        
        // Debug: Log services data
        // if (allServices.length > 0 && import.meta.env.DEV) {
        //   console.log('Services data:', allServices);
        // }
        
        // Filter services by search
        const filteredServices = allServices.filter((service) => {
          if (!serviceSearch) return true;
          const searchLower = serviceSearch.toLowerCase();
          if ('name' in service && typeof service.name === 'string') {
            return service.name.toLowerCase().includes(searchLower);
          }
          return false;
        });

        // Paginate services
        const totalServicePages = Math.ceil(filteredServices.length / itemsPerPage);
        const serviceStartIndex = (servicePage - 1) * itemsPerPage;
        const paginatedServices = filteredServices.slice(serviceStartIndex, serviceStartIndex + itemsPerPage);

        if (allServices.length === 0) {
          return (
            <div className="text-center py-8">
              <p className="text-neutral-medium font-body">
                {t('temporaryNumbers.purchase.noServices')}
              </p>
            </div>
          );
        }

        return (
          <div className="space-y-4">
            <div>
              <p className="text-neutral-medium font-body mb-4">
                {t('temporaryNumbers.purchase.selectService')}
              </p>
              
              {/* Search Input */}
              <div className="mb-4">
                <input
                  type="text"
                  value={serviceSearch}
                  onChange={(e) => {
                    setServiceSearch(e.target.value);
                    setServicePage(1);
                  }}
                  placeholder={t('temporaryNumbers.purchase.searchService')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent transition-colors"
                />
              </div>

              {/* Services Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 min-h-[300px]">
                {paginatedServices.map((service, index) => (
                  <ServiceCard
                    key={index}
                    service={service}
                    serverId={server?.serverId ?? 0}
                    isSelected={
                      selectedService !== null &&
                      JSON.stringify(selectedService) === JSON.stringify(service)
                    }
                    onSelect={handleServiceSelect}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalServicePages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-dark">
                  <button
                    onClick={() => setServicePage((p) => Math.max(1, p - 1))}
                    disabled={servicePage === 1}
                    className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('common.previous')}
                  </button>
                  <span className="text-neutral-medium font-body">
                    {t('common.page')} {servicePage} {t('common.of')} {totalServicePages}
                  </span>
                  <button
                    onClick={() => setServicePage((p) => Math.min(totalServicePages, p + 1))}
                    disabled={servicePage === totalServicePages}
                    className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('common.next')}
                  </button>
                </div>
              )}

              {filteredServices.length === 0 && serviceSearch && (
                <div className="text-center py-8">
                  <p className="text-neutral-medium font-body">
                    {t('temporaryNumbers.purchase.noResults')}
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      }

      case 'location': {
        if (isLoadingLocations) {
          return (
            <div className="text-center py-8">
              <p className="text-neutral-medium font-body animate-pulse">
                {t('common.loading')}
              </p>
            </div>
          );
        }

        const allLocations = locationsData?.data.locations || [];
        const locationType = locationsData?.data.locationType || 'countries';

        // Debug: Log locations data and query state
        if (import.meta.env.DEV && server?.serverId === 1) {
          console.log('Location step render:', {
            allLocations,
            locationsData,
            isLoadingLocations,
            locationsQueryParams,
            hasData: !!locationsData,
            error: locationsData === undefined && !isLoadingLocations,
          });
        }

        // Filter locations by search
        const filteredLocations = allLocations.filter((location) => {
          if (!locationSearch) return true;
          const searchLower = locationSearch.toLowerCase();
          if ('country' in location) {
            return location.country.toLowerCase().includes(searchLower) ||
                   location.country_id.toLowerCase().includes(searchLower);
          }
          if ('name' in location) {
            const nameMatch = location.name.toLowerCase().includes(searchLower);
            const shortNameMatch = 'shortName' in location && location.shortName.toLowerCase().includes(searchLower);
            const codeMatch = 'code' in location && location.code.toLowerCase().includes(searchLower);
            const shortNameSnakeMatch = 'short_name' in location && typeof (location as { short_name?: string }).short_name === 'string' &&
              (location as { short_name: string }).short_name.toLowerCase().includes(searchLower);
            return nameMatch || shortNameMatch || codeMatch || shortNameSnakeMatch;
          }
          return false;
        });

        // Paginate locations
        const totalLocationPages = Math.ceil(filteredLocations.length / itemsPerPage);
        const locationStartIndex = (locationPage - 1) * itemsPerPage;
        const paginatedLocations = filteredLocations.slice(locationStartIndex, locationStartIndex + itemsPerPage);

        if (allLocations.length === 0) {
          return (
            <div className="text-center py-8">
              <p className="text-neutral-medium font-body">
                {isCountryServer ? t('temporaryNumbers.purchase.noCountries') : t('temporaryNumbers.purchase.noStates')}
              </p>
            </div>
          );
        }

        return (
          <div className="space-y-4">
            <div>
              <p className="text-neutral-medium font-body mb-4">
                {isCountryServer ? t('temporaryNumbers.purchase.selectCountry') : t('temporaryNumbers.purchase.selectState')}
              </p>
              {server?.serverId === 5 && (
                <p className="text-xs text-yellow-500 font-body mb-4">
                  {t('temporaryNumbers.purchase.providerCostNote')}
                </p>
              )}
              
              {/* Search Input */}
              <div className="mb-4">
                <input
                  type="text"
                  value={locationSearch}
                  onChange={(e) => {
                    setLocationSearch(e.target.value);
                    setLocationPage(1);
                  }}
                  placeholder={isCountryServer ? t('temporaryNumbers.purchase.searchCountry') : t('temporaryNumbers.purchase.searchState')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent transition-colors"
                />
              </div>

              {/* Locations Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 min-h-[300px]">
                {paginatedLocations.map((location, index) => (
                  <LocationCard
                    key={index}
                    location={location}
                    locationType={locationType}
                    isSelected={
                      selectedLocation !== null &&
                      JSON.stringify(selectedLocation) === JSON.stringify(location)
                    }
                    onSelect={handleLocationSelect}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalLocationPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-dark">
                  <button
                    onClick={() => setLocationPage((p) => Math.max(1, p - 1))}
                    disabled={locationPage === 1}
                    className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('common.previous')}
                  </button>
                  <span className="text-neutral-medium font-body">
                    {t('common.page')} {locationPage} {t('common.of')} {totalLocationPages}
                  </span>
                  <button
                    onClick={() => setLocationPage((p) => Math.min(totalLocationPages, p + 1))}
                    disabled={locationPage === totalLocationPages}
                    className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('common.next')}
                  </button>
                </div>
              )}

              {filteredLocations.length === 0 && locationSearch && (
                <div className="text-center py-8">
                  <p className="text-neutral-medium font-body">
                    {t('temporaryNumbers.purchase.noResults')}
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      }

      case 'provider': {
        const providers = providersData?.data.providers || [];

        if (isLoadingProviders) {
          return (
            <div className="text-center py-8">
              <p className="text-neutral-medium font-body animate-pulse">
                {t('common.loading')}
              </p>
            </div>
          );
        }

        return (
          <div className="space-y-4">
            <p className="text-neutral-medium font-body">
              {t('temporaryNumbers.purchase.selectProviderOptional')}
            </p>
            <p className="text-xs text-yellow-500 font-body -mt-2">
              {t('temporaryNumbers.purchase.providerCostNote')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 min-h-[220px]">
              {providers.map((provider) => (
                <button
                  key={provider.id}
                  type="button"
                  onClick={() => handleProviderSelect(provider)}
                  className={`w-full bg-neutral-dark/50 border rounded-lg p-4 text-left hover:shadow-lg transition-all duration-300 ${
                    selectedProvider?.id === provider.id
                      ? 'border-brand-accent shadow-brand-accent/20'
                      : 'border-neutral-dark hover:border-brand-accent/50'
                  }`}
                >
                  <p className="text-neutral-light font-heading font-semibold">{provider.name}</p>
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => {
                setSelectedProvider(null);
                setCurrentStep('confirm');
              }}
              className="px-4 py-2 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
            >
              {t('temporaryNumbers.purchase.skipProvider')}
            </button>
          </div>
        );
      }

      case 'confirm': {
        const finalPrice = getFinalPrice();
        
        // Debug: Log confirm step data
        if (import.meta.env.DEV && server?.serverId === 1) {
          console.log('Confirm step - selectedLocation:', selectedLocation);
          console.log('Has country:', selectedLocation ? 'country' in selectedLocation : false);
        }
        
        return (
          <div className="space-y-6">
            <div className="bg-neutral-dark/30 rounded-lg p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-neutral-medium font-body">
                  {t('temporaryNumbers.details.service')}
                </span>
                <span className="text-neutral-light font-body">
                  {'name' in selectedService! && typeof selectedService.name === 'string'
                    ? selectedService.name
                    : ''}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-medium font-body">
                  {isCountryServer ? t('temporaryNumbers.details.country') : t('temporaryNumbers.details.state')}
                </span>
                <span className="text-neutral-light font-body">
                  {(server?.serverId === 5 || server?.serverId === 6) && summaryLocation
                    ? summaryLocation
                    : selectedLocation && 'country' in selectedLocation
                    ? (selectedLocation as { country: string }).country
                    : selectedLocation && 'name' in selectedLocation
                    ? (selectedLocation as { name: string }).name
                    : ''}
                </span>
              </div>
              {(server?.serverId === 5 && selectedProvider) && (
                <>
                  <div className="flex justify-between">
                    <span className="text-neutral-medium font-body">Provider</span>
                    <span className="text-neutral-light font-body">{selectedProvider.name}</span>
                  </div>
                  <p className="text-xs text-yellow-500 font-body">
                    {t('temporaryNumbers.purchase.providerCostNote')}
                  </p>
                </>
              )}
              {finalPrice !== null && (
                <div className="flex justify-between pt-3 border-t border-neutral-dark">
                  <span className="text-lg font-heading font-semibold text-neutral-light">
                    {t('temporaryNumbers.purchase.price')}
                  </span>
                  <span className="text-2xl font-heading font-bold text-brand-accent">
                    {finalPrice.toFixed(2)} {summaryCurrency}
                  </span>
                </div>
              )}
            </div>

            <div className="bg-neutral-dark/30 rounded-lg p-4">
              <div className="flex justify-between mb-2">
                <span className="text-neutral-medium font-body">
                  {t('temporaryNumbers.purchase.balance')}
                </span>
                <span className="text-neutral-light font-body">
                  {userBalance.toFixed(2)} {summaryCurrency}
                </span>
              </div>
              {finalPrice !== null && (
                <div className="flex justify-between pt-2 border-t border-neutral-dark">
                  <span className="text-neutral-medium font-body">
                    {t('temporaryNumbers.purchase.remaining')}
                  </span>
                  <span
                    className={`font-body ${
                      userBalance - finalPrice >= 0
                        ? 'text-green-500'
                        : 'text-red-500'
                    }`}
                  >
                    {(userBalance - finalPrice).toFixed(2)} {summaryCurrency}
                  </span>
                </div>
              )}
            </div>

            {(server?.serverId === 5 || server?.serverId === 6) && isLoadingSummary && (
              <p className="text-sm text-neutral-medium font-body animate-pulse">
                {t('common.loading')}
              </p>
            )}

            {finalPrice !== null && userBalance < finalPrice && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4">
                <p className="text-sm text-red-500 font-body">
                  {t('temporaryNumbers.purchase.insufficientBalance')}
                </p>
              </div>
            )}
          </div>
        );
      }

      default: {
        return null;
      }
    }
  };

  const getStepNumber = (): number => {
    switch (currentStep) {
      case 'server':
        return 1;
      case 'service':
        return 2;
      case 'location':
        return 3;
      case 'provider':
        return 4;
      case 'confirm':
        return totalSteps;
      default:
        return 1;
    }
  };

  return (
    <div className="space-y-6">
      {/* Progress Indicator */}
      <div className="flex items-center justify-between mb-6">
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
          <div key={step} className="flex items-center flex-1">
            <div className="flex flex-col items-center flex-1">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-heading font-semibold transition-all duration-300 ${
                  step <= getStepNumber()
                    ? 'bg-brand-accent text-brand-primary'
                    : 'bg-neutral-dark text-neutral-medium'
                }`}
              >
                {step}
              </div>
              <p className="text-xs text-neutral-medium font-body mt-2 text-center">
                {step === 4 && server?.serverId === 5
                  ? t('temporaryNumbers.purchase.step4Provider')
                  : step === 3 && server
                  ? isCountryServer
                    ? t('temporaryNumbers.purchase.step3Country')
                    : t('temporaryNumbers.purchase.step3State')
                  : step === totalSteps
                  ? t('temporaryNumbers.purchase.confirmPurchase')
                  : t(`temporaryNumbers.purchase.step${step}`)}
              </p>
            </div>
            {step < totalSteps && (
              <div
                className={`h-1 flex-1 mx-2 transition-all duration-300 ${
                  step < getStepNumber() ? 'bg-brand-accent' : 'bg-neutral-dark'
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
        <div className="min-h-[300px]">{renderStep()}</div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between pt-6 border-t border-neutral-dark">
        <button
          onClick={currentStep === 'server' ? onCancel : handleBack}
          className="px-6 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
        >
          {currentStep === 'server' ? t('common.cancel') : t('common.back')}
        </button>

        {(currentStep === 'location' && (server?.serverId === 5 || server?.serverId === 6)) && (
          <button
            type="button"
            onClick={handleSkipLocation}
            className="px-6 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
          >
            {t('temporaryNumbers.purchase.skipLocation')}
          </button>
        )}

        {currentStep === 'provider' && (
          <button
            type="button"
            onClick={() => setCurrentStep('confirm')}
            className="px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
          >
            {t('temporaryNumbers.purchase.continue')}
          </button>
        )}

        {currentStep === 'confirm' && (
          <button
            onClick={() => {
              void handlePurchase();
            }}
            disabled={isPurchasing || isLoadingSummary || (getFinalPrice() !== null && userBalance < getFinalPrice()!)}
            className="px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isPurchasing ? (
              <>
                <ButtonSpinner size="xs" />
                <span>{t('common.loading')}</span>
              </>
            ) : (
              t('temporaryNumbers.purchase.confirmPurchase')
            )}
          </button>
        )}
      </div>
    </div>
  );
}

